import { ObjectMapper } from "json-object-mapper";

export interface AbsenteismTacticalCompanyTotalModel {
  companyCNPJ: string;
  totalPredictedHours: number;
  totalAbsentHours: number;
}

export interface AbsenteismTacticalInconsistencyModel {
  employeeKey: string;
  registration: string;
  name: string;
  companyCNPJ: string;
  companyName: string;
  costCenterCode: string;
  costCenterName: string;
  yearMonth: string;
  date: string;
  totalAbsentHours: number;
  explicitHours: number;
  differenceHours: number;
  reasonDescription: string;
}

export interface AbsenteismTacticalResponseModel {
  employees: AbsenteismTacticalEmployeeModel[];
  companyTotals?: AbsenteismTacticalCompanyTotalModel[];
  currentPercent?: number;
  inconsistencies?: AbsenteismTacticalInconsistencyModel[];
}

export class AbsenteismTacticalCompetenceModel {
  yearMonth: string = "";
  predictedHours: number = 0;
}

export class AbsenteismTacticalEmployeeOccurrenceModel {
  yearMonth: string = "";
  date: string = "";
  reason: string = "";
  absentHours: number = 0;
}

export class AbsenteismTacticalEmployeeModel {
  empKey: string = "";
  registration: string = "";
  companyCNPJ: string = "";
  name: string = "";
  companyName: string = "";
  costCenterCode: string = "";
  costCenterName: string = "";
  groupCC: string = "";
  jobTitleDesc: string = "";
  shiftDesc: string = "";
  status: string = "";
  competences: AbsenteismTacticalCompetenceModel[] = [];
  occurrences: AbsenteismTacticalEmployeeOccurrenceModel[] = [];

  static create(input: any): AbsenteismTacticalEmployeeModel {
    const model = ObjectMapper.deserialize<AbsenteismTacticalEmployeeModel>(
      AbsenteismTacticalEmployeeModel,
      input
    );
    if (!model.empKey?.trim()) {
      model.empKey = String(input?.empKey ?? input?.employeeKey ?? "").trim();
    }
    if (!model.empKey?.trim()) {
      model.empKey = `${model.registration ?? ""}|${model.companyCNPJ ?? ""}`.trim();
    }
    if (!model.status?.trim()) {
      model.status = String(input?.status ?? input?.Status ?? "").trim();
    }
    if (!model.competences?.length && Array.isArray(input?.competences)) {
      model.competences = input.competences.map((c: any) => {
        const comp = new AbsenteismTacticalCompetenceModel();
        comp.yearMonth = String(c?.yearMonth ?? "");
        comp.predictedHours = Number(c?.predictedHours) || 0;
        return comp;
      });
    }
    return model;
  }

  public toJSON(): string {
    return ObjectMapper.serialize(this) as string;
  }
}

export class AbsenteismTacticalOccurrenceModel {
  empKey: string = "";
  registration: string = "";
  name: string = "";
  companyName: string = "";
  costCenterCode: string = "";
  costCenterName: string = "";
  groupCC: string = "";
  jobTitleDesc: string = "";
  shiftDesc: string = "";
  status: string = "";
  yearMonth: string = "";
  date: string = "";
  reason: string = "";
  absentHours: number = 0;

  static create(input: any): AbsenteismTacticalOccurrenceModel {
    return ObjectMapper.deserialize<AbsenteismTacticalOccurrenceModel>(
      AbsenteismTacticalOccurrenceModel,
      input
    );
  }

  static fromEmployeeAndOccurrence(
    emp: AbsenteismTacticalEmployeeModel,
    occ: AbsenteismTacticalEmployeeOccurrenceModel
  ): AbsenteismTacticalOccurrenceModel {
    const o = new AbsenteismTacticalOccurrenceModel();
    o.empKey = getEmployeeKey(emp);
    o.registration = emp.registration ?? "";
    o.name = emp.name ?? "";
    o.companyName = emp.companyName ?? "";
    o.costCenterCode = emp.costCenterCode ?? "";
    o.costCenterName = emp.costCenterName ?? "";
    o.groupCC = emp.groupCC ?? "";
    o.jobTitleDesc = emp.jobTitleDesc ?? "";
    o.shiftDesc = emp.shiftDesc ?? "";
    o.status = emp.status ?? "";
    o.yearMonth = occ.yearMonth ?? "";
    o.date = occ.date ?? "";
    o.reason = occ.reason ?? "";
    o.absentHours = occ.absentHours ?? 0;
    return o;
  }
}

export function getEmployeeKey(emp: AbsenteismTacticalEmployeeModel): string {
  const key = emp.empKey?.trim();
  if (key) {
    return key;
  }
  const fallback = `${emp.registration ?? ""}|${emp.companyCNPJ ?? ""}`.trim();
  return fallback || (emp.registration ?? "").trim();
}

export function getPredictedHours(
  employee: AbsenteismTacticalEmployeeModel | undefined,
  yearMonth: string
): number {
  if (!employee) {
    return 0;
  }
  return employee.competences?.find((c) => c.yearMonth === yearMonth)?.predictedHours ?? 0;
}

export function sumPredictedHours(
  employee: AbsenteismTacticalEmployeeModel | undefined,
  yearMonthFilter?: string | null
): number {
  if (!employee?.competences?.length) {
    return 0;
  }
  const competences = yearMonthFilter
    ? employee.competences.filter((c) => c.yearMonth === yearMonthFilter)
    : employee.competences;
  return competences.reduce((sum, c) => sum + (c.predictedHours ?? 0), 0);
}

export function flattenAbsenteismTacticalEmployees(
  employees: AbsenteismTacticalEmployeeModel[]
): AbsenteismTacticalOccurrenceModel[] {
  const out: AbsenteismTacticalOccurrenceModel[] = [];
  for (const emp of employees ?? []) {
    const occurrences = emp.occurrences ?? [];
    for (let i = 0; i < occurrences.length; i++) {
      out.push(AbsenteismTacticalOccurrenceModel.fromEmployeeAndOccurrence(emp, occurrences[i]));
    }
  }
  return out;
}

/** `distributed` = visão rateada; `percent` = visão geral (consolidada por linha). */
export type AbsenteismTacticalViewMode = "distributed" | "percent";

/** Opções padrão (labels); o layout JSON pode sobrescrever via `viewModes`. */
export const ABSENTEISM_TACTICAL_VIEW_MODE_OPTIONS: { label: string; value: AbsenteismTacticalViewMode }[] = [
  { label: "Visão geral", value: "percent" },
  { label: "Visão rateada", value: "distributed" },
];

export function isAbsenteismTacticalViewMode(value: unknown): value is AbsenteismTacticalViewMode {
  return value === "distributed" || value === "percent";
}

export function computeAbsenteeismPercent(absentHours: number, predictedHours: number): number {
  if (!predictedHours || predictedHours <= 0) {
    return 0;
  }
  return (absentHours / predictedHours) * 100;
}

/**
 * Base do % Abs. na visão rateada (`distributed`).
 * Preferência: companyTotals da API. Fallback: soma das competences dos employees.
 * Multi-empresa sem seleção: média (soma ÷ qtd. de empresas).
 */
export function computeRateadaBasePredictedHours(
  employees: AbsenteismTacticalEmployeeModel[],
  options?: {
    useCompanyAverage?: boolean;
    companyTotals?: AbsenteismTacticalCompanyTotalModel[];
    selectedCompanyCNPJ?: string | null;
  }
): number {
  const fromTotals = computeBaseFromCompanyTotals(options?.companyTotals, {
    useCompanyAverage: options?.useCompanyAverage,
    selectedCompanyCNPJ: options?.selectedCompanyCNPJ,
  });
  if (fromTotals !== null) {
    return fromTotals;
  }

  const list = employees ?? [];
  const total = list.reduce((sum, emp) => sum + sumPredictedHours(emp), 0);
  if (!options?.useCompanyAverage || total <= 0) {
    return total;
  }

  const companyKeys = new Set<string>();
  list.forEach((emp) => {
    const key = (emp.companyCNPJ ?? "").trim() || (emp.companyName ?? "").trim();
    if (key) {
      companyKeys.add(key);
    }
  });
  const companyCount = companyKeys.size;
  if (companyCount <= 1) {
    return total;
  }
  return total / companyCount;
}

export function normalizeAbsenteismCompanyTotals(raw: unknown): AbsenteismTacticalCompanyTotalModel[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((item: Record<string, unknown>) => ({
    companyCNPJ: String(item?.companyCNPJ ?? item?.CompanyCNPJ ?? "").trim(),
    totalPredictedHours: Number(item?.totalPredictedHours ?? item?.TotalPredictedHours) || 0,
    totalAbsentHours: Number(item?.totalAbsentHours ?? item?.TotalAbsentHours) || 0,
  }));
}

export function normalizeAbsenteismInconsistencies(raw: unknown): AbsenteismTacticalInconsistencyModel[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((item: Record<string, unknown>) => {
    const totalAbsentHours = Number(item?.totalAbsentHours ?? item?.TotalAbsentHours) || 0;
    const explicitHours = Number(item?.explicitHours ?? item?.ExplicitHours) || 0;
    const differenceRaw = item?.differenceHours ?? item?.DifferenceHours;
    const differenceHours =
      differenceRaw !== undefined && differenceRaw !== null && String(differenceRaw).trim() !== ""
        ? Number(differenceRaw) || 0
        : totalAbsentHours - explicitHours;

    const dateRaw = item?.date ?? item?.Date;
    let date = "";
    if (dateRaw instanceof Date) {
      date = dateRaw.toISOString();
    } else if (dateRaw != null) {
      date = String(dateRaw).trim();
    }

    return {
      employeeKey: String(item?.employeeKey ?? item?.EmployeeKey ?? item?.empKey ?? "").trim(),
      registration: String(item?.registration ?? item?.Registration ?? "").trim(),
      name: String(item?.name ?? item?.Name ?? "").trim(),
      companyCNPJ: String(item?.companyCNPJ ?? item?.CompanyCNPJ ?? "").trim(),
      companyName: String(item?.companyName ?? item?.CompanyName ?? "").trim(),
      costCenterCode: String(item?.costCenterCode ?? item?.CostCenterCode ?? "").trim(),
      costCenterName: String(item?.costCenterName ?? item?.CostCenterName ?? "").trim(),
      yearMonth: String(item?.yearMonth ?? item?.YearMonth ?? "").trim(),
      date,
      totalAbsentHours,
      explicitHours,
      differenceHours,
      reasonDescription: String(
        item?.reasonDescription ?? item?.ReasonDescription ?? item?.reason ?? ""
      ).trim(),
    };
  });
}

function normalizeCompanyCNPJ(cnpj: string | null | undefined): string {
  return String(cnpj ?? "").replace(/\D/g, "");
}

function computeBaseFromCompanyTotals(
  companyTotals: AbsenteismTacticalCompanyTotalModel[] | undefined,
  options?: { useCompanyAverage?: boolean; selectedCompanyCNPJ?: string | null }
): number | null {
  const totals = companyTotals ?? [];
  if (!totals.length) {
    return null;
  }

  const selectedDigits = normalizeCompanyCNPJ(options?.selectedCompanyCNPJ);
  if (selectedDigits) {
    const match = totals.find((t) => normalizeCompanyCNPJ(t.companyCNPJ) === selectedDigits);
    if (match) {
      return Number(match.totalPredictedHours) || 0;
    }
  }

  const sum = totals.reduce((acc, t) => acc + (Number(t.totalPredictedHours) || 0), 0);
  if (options?.useCompanyAverage && totals.length > 1) {
    return sum / totals.length;
  }
  return sum;
}
